import React from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';

import { cn } from '../../cn';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from './Card';
import { Button } from './button';
import { useLanguage } from '../../providers/LanguageContext'

type PageErrorBoundaryProps = {
  children: React.ReactNode;
};

function sanitizeErrorMessage(error: unknown): string {
  const fallback = 'Unbekannter Fehler';

  if (!error) return fallback;

  let rawMessage: string | null = null;

  if (error instanceof Error && typeof error.message === 'string') {
    rawMessage = error.message;
  } else if (typeof error === 'string') {
    rawMessage = error;
  }

  if (!rawMessage) return fallback;

  const normalized = rawMessage.replace(/\s+/g, ' ').trim();
  if (!normalized) return fallback;

  const maxLength = 300;
  if (normalized.length <= maxLength) return normalized;

  return `${normalized.slice(0, maxLength)}…`;
}

function PageErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const { t } = useLanguage()
  const message = sanitizeErrorMessage(error);
  const isDev = typeof import.meta !== 'undefined' && import.meta.env?.DEV;
  // `error` is widened to `{}`/`unknown` under some react-error-boundary type
  // resolutions, so narrow to Error before touching `.stack` (TS2339-safe).
  const stack = error instanceof Error ? error.stack : undefined;

  return (
    <section
      className="my-4 min-w-0"
      aria-live="assertive"
      aria-atomic="true"
    >
      <Card
        role="alert"
        className={cn(
          'border border-danger/40 bg-background text-foreground',
          'shadow-sm max-w-full sm:max-w-lg'
        )}
      >
        <CardHeader>
          <CardTitle className="text-danger">
            Etwas ist schiefgelaufen
          </CardTitle>
          <CardDescription>
            {message}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isDev && stack && (
            <details className="mt-2 text-xs text-muted-foreground">
              <summary className="cursor-pointer select-none">
                Fehlerdetails (nur Entwicklung)
              </summary>
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all overscroll-contain">
                {stack}
              </pre>
            </details>
          )}
        </CardContent>
        <CardFooter className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            size="md"
            onClick={resetErrorBoundary}
            data-voice-role="action"
          >
            Erneut versuchen
          </Button>
          <a
            href="/"
            className="text-sm text-accent underline underline-offset-2"
            aria-label={t('ui.errorBoundary.returnToDashboard')}
          >
            Zum Dashboard
          </a>
        </CardFooter>
      </Card>
    </section>
  );
}

export function PageErrorBoundary({ children }: PageErrorBoundaryProps) {
  return (
    <ErrorBoundary FallbackComponent={PageErrorFallback} onError={(error, info) => {
      console.error('[PageErrorBoundary]', error, info.componentStack)
      // D-33: Report to Sentry. ui-components has no Sentry dep,
      // so we use the global captureException set by the host app's Sentry.init()
      const w = window as Window & { __sentryCapture?: (e: unknown, ctx?: unknown) => void }
      if (typeof w.__sentryCapture === 'function') {
        w.__sentryCapture(error, {
          contexts: { react: { componentStack: info.componentStack ?? '' } },
        })
      }
    }}>
      {children}
    </ErrorBoundary>
  );
}

